import React, { useState } from 'react';
import { Form, useNavigate } from 'react-router-dom';
import { 
Box, 
Button, 
Container, 
CssBaseline, 
FormControl, 
InputAdornment, 
InputLabel, 
MenuItem, 
Select, 
TextField, 
Typography,
Fade,
Slide,
Zoom
} from '@mui/material';
import { 
AccountCircle, 
Lock, 
Person, 
Login as LoginIcon 
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import logoIcon from "../img/logo-1.png";
import useCustomToast from '../hooks/useToast';
import { TEXT_CONNECTION_ERROR, TEXT_ERROR_INPUTS } from '../utilities/Text';
import { APIUserLogIn } from '../utilities/APIs';
import axios from 'axios';
import { headers } from '../utilities/APIs';

const Login = () => {
    const showToast = useCustomToast();
    const [formData, setFormData] = useState({
        role: '',
        username: '',
        password: '',
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    }
};


    const handleSubmit = async (e) => {
        e.preventDefault();
        let username = formData.username;
        let password = formData.password;
        let role = formData.role;

        if (!password || !username || !role) {
        showToast("Warning", TEXT_ERROR_INPUTS, "warning");
        return;
        }
        setIsLoading(true);

        try {
            let formDataToSend = new FormData ();
            formDataToSend.append("email",username);
            formDataToSend.append("password",password);
        const response = await axios.post(APIUserLogIn,formDataToSend, headers);

        if (response.data.status === "success") {
            const userData = response.data.data;

            const userInfo = {
                name: userData.name,
                email: userData.email,
                role: userData.role,
                id: userData.id,
                loggedIn: true
            };
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
            showToast("Success", "Login successful", "success");
            navigate("/contact-us");
        } else {
            showToast("Error", response.data.message || "Login failed", "error");
        }
        } catch (error) {
            if (error.response) {
                        // Handle validation errors (422 status)
                        if (error.response.status === 422) {
                            const errors = error.response.data.errors;
                            let errorMessages = [];
                            // Collect all validation error messages
                            for (const field in errors) {
                                errorMessages.push(...errors[field]);
                            }
                            // Show all error messages in toast
                            showToast(
                                "Validation Error",
                                errorMessages.join("\n"),
                                "error"
                            );
                        }
                        // Handle other API errors
                        else {
                            showToast(
                                "Error",
                                error.response.data.message || "An error occurred",
                                "error"
                            );
                        }
                    }
                    // Handle network errors
                    else if (error.code === "ERR_NETWORK") {
                        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
                    } 
                    // Handle other unexpected errors
                    else {
                        showToast("Error", "An unexpected error occurred", "error");
                    }

        } finally {
            setIsLoading(false);
        }
    };

return (
    <AnimatedContainer
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5 }}
    >
    <CssBaseline />
    <Slide in direction="down" timeout={500}>
        <LoginCard>
        <Zoom in timeout={800}>
            <LogoContainer>
            <img
                src={logoIcon}
                alt="Company Logo"
            />
            </LogoContainer>
        </Zoom>
        
        <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
            fontWeight: 'bold',
            mb: 2,
            background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            }}
        >
            Welcome Back
        </Typography>
        
        <Typography 
            variant="body2" 
            color="textSecondary" 
            sx={{ mb: 4 }}
        >
            Please login to access your account
        </Typography>
        
        <LoginForm onSubmit={handleSubmit}>

            <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                label="Role"
                startAdornment={
                <InputAdornment position="start">
                    <Person color={'action'} />
                </InputAdornment>
                }
                sx={{
                '& .MuiSelect-select': {
                    paddingLeft: '12px',
                },
                }}
            >
                <MenuItem value="" disabled>
                Select your role
                </MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
            </Select>
            </FormControl>
            
            <TextField
            margin="normal"
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={formData.username}
            onChange={handleChange}
           
            InputProps={{
                startAdornment: (
                <InputAdornment position="start">
                    <AccountCircle color={'action'} />
                </InputAdornment>
                ),
            }}
            sx={{ mb: 3 }}
            />
            
            <TextField
            margin="normal"
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
                startAdornment: (
                <InputAdornment position="start">
                    <Lock color={'action'} />
                </InputAdornment>
                ),
            }}
            sx={{ mb: 2 }}
            />
            
            <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            startIcon={<LoginIcon />}
            sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
            }}
            >
            {isLoading ? 'Signing In...' : 'Sign In'}
            </SubmitButton>
            
            <Box sx={{ 
            mt: 2,
            textAlign: 'center',
            '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                textDecoration: 'underline',
                },
            },
            }}>
            <Typography variant="body2">
                <a href="/forgot-password">Forgot password?</a>
            </Typography>
            </Box>
        </LoginForm>
        </LoginCard>
    </Slide>
    </AnimatedContainer>
);
};

export default Login;


// Styled components
const AnimatedContainer = styled(motion.div)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const LoginCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
    backgroundColor: theme.palette.background.paper || '#ffffff',
    width: '100%',
    maxWidth: '450px',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
        maxWidth: '90%',
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    '& img': {
        width: '300px',
        height: '100px',
        borderRadius: '10',
        // objectFit: 'cover',
        // boxShadow: theme.shadows[4],
        [theme.breakpoints.down('sm')]: {
        width: '90px',
        height: '90px',
        },
    },
}));

const LoginForm = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(2),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 2,
    fontWeight: 'bold',
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
    },
}));
